import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { X, DollarSign, FileText, Truck, Shield, Wrench, Package, TreePine, Car, HardHat, Tent, Eye, Ship, Percent, ClipboardList, ExternalLink, Loader2, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { DesignEstimatingData, GlobalCosts, ProposalBondType, ProposalBonding, ProposalNodeSet } from '../../types';
import {
    BOND_TYPE_LABELS,
    BOND_TYPE_ORDER,
    activeBondingLines,
    computeBondingAmount,
    createBondingLine,
    createEmptyProposalBonding,
    effectiveBondingPercent,
    formatBondingPercent,
} from '../../utils/proposalBonding';
import { storage } from '../../utils/firebase';
import { isEditableTarget } from '../../utils/isEditableTarget';
import {
    getEffectiveProjectOverheadPercent,
    createEmptyGlobalCosts,
    DEFAULT_PROJECT_OVERHEAD_PERCENT,
} from '../../utils/proposalCalculations';
import {
    estimatingRaw,
    hasDesignerInvoicePreview,
    isDesignEstimatingLocked,
    resolveCrmHourlyRate,
    resolveDesignerHourlyRate,
    stampDesignEstimatingTotal,
    unlockDesignEstimating,
    type DesignRateInputs,
} from '../../utils/proposalDesignBreakdown';
import './GlobalCostsModal.css';

interface GlobalCostsModalProps {
    globalCosts?: GlobalCosts;
    nodeSets: ProposalNodeSet[];
    designEstimating?: DesignEstimatingData | null;
    rates?: DesignRateInputs | null;
    proposalStatus?: string | null;
    /** Client Total Investment before bonding (post-margin, post-discount) — drives the bonding preview only. */
    clientSubtotal?: number;
    onSave: (globalCosts: GlobalCosts, designEstimating: DesignEstimatingData) => void;
    onClose: () => void;
}

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);

/** Legacy-safe: project overhead stays off at 0% when a proposal has no saved globalCosts. */
const DEFAULT_GLOBAL_COSTS: GlobalCosts = createEmptyGlobalCosts();

type GlobalCostLineKey = keyof Omit<GlobalCosts, 'totalGlobalCosts' | 'projectOverheadPercentEnabled' | 'projectOverheadPercent' | 'bonding'>;

// Config for each cost line item
const COST_ITEMS: Array<{
    key: GlobalCostLineKey;
    label: string;
    description: string;
    icon: React.ReactNode;
}> = [
        { key: 'baselineTravel', label: 'Baseline Travel', description: 'General travel expenses for the whole project', icon: <Truck size={16} /> },
        { key: 'additionalTravel', label: 'Additional Travel', description: 'Extra travel costs beyond per-structure travel', icon: <Truck size={16} /> },
        { key: 'globalFreightInbound', label: 'Freight — Inbound', description: 'Inbound freight (materials to shop/jobsite)', icon: <Ship size={16} /> },
        { key: 'globalFreightOutbound', label: 'Freight — Outbound', description: 'Outbound freight (finished product to jobsite)', icon: <Ship size={16} /> },
        { key: 'equipmentRental', label: 'Long-Term Equipment Rental', description: 'Cranes, lifts, scaffolding, etc.', icon: <Package size={16} /> },
        { key: 'additionalGear', label: 'Additional Gear Budget', description: 'Specialty tools and gear for the project', icon: <Wrench size={16} /> },
        { key: 'ppeBudget', label: 'PPE Budget', description: 'Personal protective equipment for crew', icon: <HardHat size={16} /> },
        { key: 'jobsiteIncidentals', label: 'Jobsite Incidentals', description: 'Bracing lumber, food, drinks, popup tents, etc.', icon: <Tent size={16} /> },
        { key: 'additionalInsurance', label: 'Additional Insurance', description: 'Job-specific insurance coverage', icon: <Shield size={16} /> },
        { key: 'damagedTimberBudget', label: 'Damaged Timber Budget', description: 'Auto-calculates 5% of total timber cost', icon: <TreePine size={16} /> },
        { key: 'jobsiteSecurity', label: 'Jobsite Security', description: 'Security personnel or services', icon: <Eye size={16} /> },
        { key: 'additionalVehicles', label: 'Additional Vehicles', description: 'Extra vehicles for the project', icon: <Car size={16} /> },
        { key: 'jobSpecificTooling', label: 'Job-Specific Tooling', description: 'Custom tools built/purchased for this job', icon: <Wrench size={16} /> },
    ];

const GlobalCostsModal: React.FC<GlobalCostsModalProps> = ({
    globalCosts: initialGlobalCosts,
    nodeSets,
    designEstimating: initialDesignEstimating,
    rates,
    proposalStatus,
    clientSubtotal = 0,
    onSave,
    onClose,
}) => {
    const [costs, setCosts] = useState<GlobalCosts>(
        () => ({ ...DEFAULT_GLOBAL_COSTS, ...(initialGlobalCosts || {}) })
    );
    // Bonding is a client pass-through, kept apart from the dollar lines so it
    // never enters totalGlobalCosts or the per-structure spread.
    const [bonding, setBonding] = useState<ProposalBonding>(
        () => ({ ...createEmptyProposalBonding(), ...(initialGlobalCosts?.bonding || {}), lines: [...(initialGlobalCosts?.bonding?.lines || [])] })
    );
    const bondingPercent = effectiveBondingPercent({ bonding });
    const bondingPreview = computeBondingAmount(clientSubtotal, bondingPercent);

    const handleBondingToggle = (on: boolean) => {
        setBonding(prev => ({
            ...prev,
            enabled: on,
            // First enable: seed the usual pair so there is something to edit.
            lines: on && prev.lines.length === 0
                ? [createBondingLine('performance', 1), createBondingLine('payment', 0.5)]
                : prev.lines,
        }));
    };
    const patchBondingLine = (id: string, patch: Partial<ProposalBonding['lines'][number]>) => {
        setBonding(prev => ({ ...prev, lines: prev.lines.map(l => (l.id === id ? { ...l, ...patch } : l)) }));
    };
    const removeBondingLine = (id: string) => {
        setBonding(prev => ({ ...prev, lines: prev.lines.filter(l => l.id !== id) }));
    };
    const addBondingLine = () => {
        setBonding(prev => {
            const used = new Set(prev.lines.map(l => l.type));
            const nextType = BOND_TYPE_ORDER.find(t => t !== 'other' && !used.has(t)) ?? 'other';
            return { ...prev, lines: [...prev.lines, createBondingLine(nextType, 0)] };
        });
    };
    const [estimating, setEstimating] = useState<DesignEstimatingData>(
        () => ({ ...(initialDesignEstimating || {}) })
    );
    const [quickLook, setQuickLook] = useState<{ url: string; name: string } | null>(null);
    const [quickLookLoading, setQuickLookLoading] = useState(false);
    const invoicePreviewArmedRef = useRef(false);
    const quickLookOpenRef = useRef(false);

    useEffect(() => {
        quickLookOpenRef.current = !!quickLook || quickLookLoading;
    }, [quickLook, quickLookLoading]);

    const invoicePreviewAvailable = hasDesignerInvoicePreview(estimating);
    const crmRateLabel = rates
        ? resolveCrmHourlyRate(estimating, rates)
        : (estimating.crmHourlyRate ?? 50);
    const designerRateLabel = rates
        ? resolveDesignerHourlyRate(estimating, rates)
        : (estimating.designerHourlyRate ?? 120);
    const markupRegions = estimating.designerInvoiceMarkupRegions || [];

    const openInvoiceQuickLook = useCallback(async () => {
        if (!hasDesignerInvoicePreview(estimating)) return;
        setQuickLookLoading(true);
        try {
            let url = estimating.designerInvoiceFileUrl || null;
            if (!url && estimating.designerInvoiceStoragePath) {
                url = await getDownloadURL(storageRef(storage, estimating.designerInvoiceStoragePath));
            }
            if (url) {
                setQuickLook({
                    url,
                    name: estimating.designerInvoiceFileName || 'Designer invoice',
                });
            }
        } catch {
            // Missing/inaccessible file — quiet fail like Finder Quick Look.
        } finally {
            setQuickLookLoading(false);
        }
    }, [estimating]);

    // Calculate total timber raw cost across all structures (for damaged timber auto-calc)
    const totalTimberRawCost = useMemo(() => {
        return nodeSets.filter(ns => ns.enabled !== false).reduce((sum, ns) => {
            const qty = ns.quantity || 1;
            return sum + (ns.materials.timberTotal || ns.materials.total || 0) * qty;
        }, 0);
    }, [nodeSets]);

    const structureRawTotal = useMemo(() => {
        const excludeStructureOverhead = getEffectiveProjectOverheadPercent(costs) > 0;
        return nodeSets.filter(ns => ns.enabled !== false).reduce((sum, ns) => {
            const qty = ns.quantity || 1;
            return sum +
                (ns.design.total +
                    ns.production.total +
                    ns.materials.total +
                    ns.freight.total +
                    (excludeStructureOverhead ? 0 : ns.overhead.total) +
                    ns.install.total +
                    ns.travel.total +
                    (excludeStructureOverhead ? 0 : (ns.install.overheadTotal || 0))) * qty;
        }, 0);
    }, [nodeSets, costs.projectOverheadPercentEnabled, costs.projectOverheadPercent]);

    // Auto-populate damaged timber budget at 5% when first enabling
    const handleToggle = (key: GlobalCostLineKey) => {
        setCosts(prev => {
            const item = prev[key];
            const newEnabled = !item.enabled;
            let newAmount = item.amount;

            // Auto-populate damaged timber at 5% of total timber cost
            if (key === 'damagedTimberBudget' && newEnabled && item.amount === 0) {
                newAmount = Math.round(totalTimberRawCost * 0.05);
            }

            return {
                ...prev,
                [key]: { ...item, enabled: newEnabled, amount: newAmount },
            };
        });
    };

    const handleAmountChange = (key: GlobalCostLineKey, value: string) => {
        const numValue = parseFloat(value) || 0;
        setCosts(prev => ({
            ...prev,
            [key]: { ...prev[key], amount: numValue },
        }));
    };

    const handleNotesChange = (key: GlobalCostLineKey, value: string) => {
        setCosts(prev => ({
            ...prev,
            [key]: { ...prev[key], notes: value },
        }));
    };

    // Calculate running total
    const runningTotal = useMemo(() => {
        return COST_ITEMS.reduce((sum, item) => {
            const costItem = costs[item.key];
            return sum + (costItem.enabled ? costItem.amount : 0);
        }, 0);
    }, [costs]);

    const estimatingLocked = isDesignEstimatingLocked(estimating, proposalStatus);
    const estimatingTotal = estimatingRaw(estimating, rates, proposalStatus);

    const projectOverheadPreview = useMemo(() => {
        if (!costs.projectOverheadPercentEnabled) return 0;
        const pct = Math.max(0, Math.min(100, Number(costs.projectOverheadPercent) || 0));
        if (pct <= 0) return 0;
        const base = structureRawTotal + runningTotal + estimatingTotal;
        return Math.round(base * (pct / 100));
    }, [costs.projectOverheadPercentEnabled, costs.projectOverheadPercent, structureRawTotal, runningTotal, estimatingTotal]);

    const structureCount = nodeSets.length;
    const perStructureDollarLines = structureCount > 0 ? runningTotal / structureCount : 0;
    const perStructureProjectOverhead =
        structureCount > 0 && projectOverheadPreview > 0 ? projectOverheadPreview / structureCount : 0;
    /** Dollar global lines + estimating + project overhead % raw (matches builder raw before margin). */
    const totalGlobalRawBeforeMargin = runningTotal + estimatingTotal + projectOverheadPreview;

    const handleProjectOverheadToggle = (on: boolean) => {
        setCosts(prev => ({
            ...prev,
            projectOverheadPercentEnabled: on,
            projectOverheadPercent: on && (!prev.projectOverheadPercent || prev.projectOverheadPercent <= 0)
                ? DEFAULT_PROJECT_OVERHEAD_PERCENT
                : (prev.projectOverheadPercent ?? 0),
        }));
    };

    const handleEstimatingChange = (patch: Partial<DesignEstimatingData>) => {
        if (estimatingLocked) return;
        setEstimating(prev => ({ ...prev, ...patch }));
    };

    const handleSave = () => {
        const nextEstimating = rates
            ? stampDesignEstimatingTotal(estimating, rates, proposalStatus)
            : estimating;
        const cleanBonding: ProposalBonding = {
            enabled: !!bonding.enabled,
            lines: bonding.lines.map(l => ({
                id: l.id,
                type: l.type,
                ...(l.type === 'other' && l.label?.trim() ? { label: l.label.trim() } : {}),
                percent: Math.max(0, Math.min(100, Number(l.percent) || 0)),
                enabled: l.enabled !== false,
            })),
            ...(bonding.clientDescription?.trim() ? { clientDescription: bonding.clientDescription.trim() } : {}),
        };
        onSave({
            ...costs,
            totalGlobalCosts: runningTotal,
            projectOverheadPercentEnabled: !!costs.projectOverheadPercentEnabled,
            projectOverheadPercent: costs.projectOverheadPercentEnabled
                ? Math.max(0, Math.min(100, Number(costs.projectOverheadPercent) || 0))
                : 0,
            // Only persist the field once bonding has been touched, so untouched
            // proposals keep their original document shape.
            ...(cleanBonding.enabled || cleanBonding.lines.length > 0 || initialGlobalCosts?.bonding
                ? { bonding: cleanBonding }
                : {}),
        }, nextEstimating);
    };

    // Keyboard: Esc closes Quick Look or modal; Space toggles invoice PDF Quick Look when $ is hovered.
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (quickLookOpenRef.current) {
                    e.preventDefault();
                    setQuickLook(null);
                    setQuickLookLoading(false);
                    return;
                }
                onClose();
                return;
            }
            if ((e.metaKey || e.ctrlKey) && e.key === 's') {
                e.preventDefault();
                handleSave();
                return;
            }
            if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
            if (isEditableTarget(e.target)) return;
            if (e.code !== 'Space') return;
            const target = e.target as HTMLElement | null;
            if (target?.tagName === 'BUTTON') return;
            if (quickLookOpenRef.current) {
                e.preventDefault();
                setQuickLook(null);
                return;
            }
            if (!invoicePreviewArmedRef.current) return;
            e.preventDefault();
            void openInvoiceQuickLook();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runningTotal, estimating, bonding, rates, proposalStatus, onClose, openInvoiceQuickLook]);

    return (
        <div className="global-costs-overlay" onClick={onClose}>
            <div className="global-costs-modal" onClick={e => e.stopPropagation()}>
                {/* Header — project overhead % lives here; dollar lines stay in the body */}
                <div className="gcm-header">
                    <div className="gcm-header-left">
                        <DollarSign size={20} />
                        <div>
                            <h2>Global Project Costs</h2>
                            <p>Optional costs for large/complex projects — absorbed into overhead</p>
                        </div>
                    </div>
                    <div
                        className={`gcm-header-overhead ${costs.projectOverheadPercentEnabled ? 'enabled' : 'disabled'}`}
                    >
                        <div className="gcm-header-overhead-row">
                            <label className="gcm-toggle-label">
                                <input
                                    type="checkbox"
                                    checked={!!costs.projectOverheadPercentEnabled}
                                    onChange={(e) => handleProjectOverheadToggle(e.target.checked)}
                                />
                                <span className="gcm-toggle-icon"><Percent size={16} /></span>
                                <span className="gcm-toggle-text">
                                    <strong>Project overhead</strong>
                                </span>
                            </label>
                            <div className="gcm-pct-input-wrap">
                                <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    step={0.1}
                                    className="gcm-cost-input gcm-pct-input"
                                    value={costs.projectOverheadPercentEnabled ? (costs.projectOverheadPercent ?? '') : ''}
                                    onChange={(e) => setCosts(prev => ({
                                        ...prev,
                                        projectOverheadPercent: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)),
                                    }))}
                                    placeholder={`${DEFAULT_PROJECT_OVERHEAD_PERCENT}`}
                                    disabled={!costs.projectOverheadPercentEnabled}
                                />
                                <span className="gcm-pct-suffix">%</span>
                            </div>
                        </div>
                        <small>
                            Applies to structure raw, estimating, and every enabled dollar line below (before margin).
                            {costs.projectOverheadPercentEnabled && projectOverheadPreview > 0
                                ? ` ≈ ${formatCurrency(projectOverheadPreview)} raw`
                                : ''}
                        </small>
                    </div>
                    <button className="gcm-close" onClick={onClose}><X size={18} /></button>
                </div>

                <div className="gcm-body">
                    <div className={`gcm-estimating ${estimatingTotal > 0 ? 'has-value' : ''}`}>
                        <div className="gcm-estimating-header">
                            <span className="gcm-toggle-icon"><ClipboardList size={16} /></span>
                            <div>
                                <strong>Estimating / modeling</strong>
                                <small>
                                    Project-level, before sign. CRM time plus designer time invoiced separately.
                                    {estimatingLocked ? ' Locked at send — later CRM time does not grow this amount.' : ''}
                                </small>
                            </div>
                            <strong className={estimatingTotal > 0 ? 'has-value' : ''}>{formatCurrency(estimatingTotal)}</strong>
                        </div>
                        {estimatingLocked && (
                            <button
                                type="button"
                                className="gcm-unlock-btn"
                                onClick={() => setEstimating(prev => unlockDesignEstimating(prev))}
                            >
                                Unlock estimating
                            </button>
                        )}
                        <div className="gcm-estimating-grid">
                            <label>
                                CRM hours
                                <input
                                    type="number"
                                    min="0"
                                    step="0.25"
                                    value={estimating.crmHours ?? ''}
                                    disabled={estimatingLocked}
                                    onChange={(e) => handleEstimatingChange({ crmHours: parseFloat(e.target.value) || 0 })}
                                />
                                <span>{formatCurrency(crmRateLabel)}/hr</span>
                            </label>
                            <label>
                                Designer hours
                                <input
                                    type="number"
                                    min="0"
                                    step="0.25"
                                    value={estimating.designerHours ?? ''}
                                    disabled={estimatingLocked}
                                    onChange={(e) => handleEstimatingChange({ designerHours: parseFloat(e.target.value) || 0 })}
                                />
                                <span>{formatCurrency(designerRateLabel)}/hr</span>
                            </label>
                            <label
                                className={invoicePreviewAvailable ? 'gcm-invoice-peekable' : undefined}
                                onMouseEnter={() => {
                                    if (invoicePreviewAvailable) invoicePreviewArmedRef.current = true;
                                }}
                                onMouseLeave={() => {
                                    invoicePreviewArmedRef.current = false;
                                }}
                                title={
                                    invoicePreviewAvailable
                                        ? 'Hover this amount and press Space to preview the invoice PDF'
                                        : undefined
                                }
                            >
                                Designer invoice ($)
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    className={invoicePreviewAvailable ? 'gcm-invoice-amount--peekable' : undefined}
                                    value={estimating.designerInvoiceAmount ?? ''}
                                    disabled={estimatingLocked}
                                    onChange={(e) => handleEstimatingChange({ designerInvoiceAmount: parseFloat(e.target.value) || 0 })}
                                    onFocus={() => {
                                        if (invoicePreviewAvailable) invoicePreviewArmedRef.current = true;
                                    }}
                                    onBlur={() => {
                                        invoicePreviewArmedRef.current = false;
                                    }}
                                />
                                {invoicePreviewAvailable ? (
                                    <span className="gcm-invoice-peek-hint">
                                        {estimating.designerInvoiceFileName || 'Invoice PDF'} · Space to preview
                                    </span>
                                ) : (
                                    <span>Recorded separately</span>
                                )}
                            </label>
                        </div>
                        {invoicePreviewAvailable && markupRegions.length > 0 && (
                            <div className="gcm-invoice-markup-summary">
                                {markupRegions.map((region, idx) => (
                                    <span key={`${region.pageNumber}-${idx}`} className="gcm-invoice-markup-chip">
                                        p{region.pageNumber}
                                        {region.label ? ` · ${region.label}` : ''}
                                        {region.amount != null ? ` · ${formatCurrency(region.amount)}` : ''}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="gcm-dollar-lines-divider">
                        <span className="gcm-dollar-lines-label">Global dollar lines</span>
                    </div>

                    {COST_ITEMS.map(item => {
                        const costItem = costs[item.key];
                        const isDamagedTimber = item.key === 'damagedTimberBudget';

                        return (
                            <div key={item.key} className={`gcm-cost-row ${costItem.enabled ? 'enabled' : 'disabled'}`}>
                                <div className="gcm-cost-header">
                                    <label className="gcm-toggle-label">
                                        <input
                                            type="checkbox"
                                            checked={costItem.enabled}
                                            onChange={() => handleToggle(item.key)}
                                        />
                                        <span className="gcm-toggle-icon">{item.icon}</span>
                                        <span className="gcm-toggle-text">
                                            <strong>{item.label}</strong>
                                            <small>{item.description}</small>
                                        </span>
                                    </label>
                                    <div className="gcm-cost-input-wrap">
                                        <span className="gcm-dollar-sign">$</span>
                                        <input
                                            type="number"
                                            className="gcm-cost-input"
                                            value={costItem.amount || ''}
                                            onChange={e => handleAmountChange(item.key, e.target.value)}
                                            placeholder="0"
                                            disabled={!costItem.enabled}
                                        />
                                    </div>
                                </div>

                                {/* Damaged timber auto-calc hint */}
                                {isDamagedTimber && costItem.enabled && (
                                    <div className="gcm-auto-hint">
                                        5% of total timber ({formatCurrency(totalTimberRawCost)}) = {formatCurrency(Math.round(totalTimberRawCost * 0.05))}
                                    </div>
                                )}

                                {/* Notes */}
                                {costItem.enabled && (
                                    <div className="gcm-notes-wrap">
                                        <FileText size={12} />
                                        <input
                                            type="text"
                                            className="gcm-notes-input"
                                            value={costItem.notes || ''}
                                            onChange={e => handleNotesChange(item.key, e.target.value)}
                                            placeholder="Notes (optional)"
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    <div className="gcm-dollar-lines-divider">
                        <span className="gcm-dollar-lines-label">Client pass-through</span>
                    </div>

                    {/* Bonding — separate client line below Installation; not in totalGlobalCosts */}
                    <div className={`gcm-cost-row gcm-bonding ${bonding.enabled ? 'enabled' : 'disabled'}`}>
                        <div className="gcm-cost-header">
                            <label className="gcm-toggle-label">
                                <input
                                    type="checkbox"
                                    checked={bonding.enabled}
                                    onChange={(e) => handleBondingToggle(e.target.checked)}
                                />
                                <span className="gcm-toggle-icon"><ShieldCheck size={16} /></span>
                                <span className="gcm-toggle-text">
                                    <strong>Bonding</strong>
                                    <small>
                                        Surety premiums billed to the client as their own line below Installation — a percent of the
                                        Total Investment, no margin, outside the global cost split. Moves with the client's selections and change orders.
                                    </small>
                                </span>
                            </label>
                            <div className="gcm-bonding-summary">
                                <strong className={bondingPercent > 0 ? 'has-value' : ''}>{formatBondingPercent(bondingPercent)}</strong>
                                {bondingPercent > 0 && clientSubtotal > 0 && (
                                    <small>≈ {formatCurrency(bondingPreview)} on {formatCurrency(clientSubtotal)}</small>
                                )}
                            </div>
                        </div>

                        {bonding.enabled && (
                            <div className="gcm-bonding-lines">
                                {bonding.lines.map(line => (
                                    <div key={line.id} className={`gcm-bonding-line ${line.enabled !== false ? '' : 'is-off'}`}>
                                        <input
                                            type="checkbox"
                                            checked={line.enabled !== false}
                                            onChange={(e) => patchBondingLine(line.id, { enabled: e.target.checked })}
                                            aria-label="Include this bond"
                                        />
                                        <select
                                            className="gcm-bonding-type"
                                            value={line.type}
                                            onChange={(e) => patchBondingLine(line.id, { type: e.target.value as ProposalBondType })}
                                        >
                                            {BOND_TYPE_ORDER.map(t => (
                                                <option key={t} value={t}>{BOND_TYPE_LABELS[t]}</option>
                                            ))}
                                        </select>
                                        {line.type === 'other' && (
                                            <input
                                                type="text"
                                                className="gcm-bonding-label"
                                                value={line.label || ''}
                                                onChange={(e) => patchBondingLine(line.id, { label: e.target.value })}
                                                placeholder="Bond name shown to client"
                                            />
                                        )}
                                        <div className="gcm-pct-input-wrap">
                                            <input
                                                type="number"
                                                min={0}
                                                max={100}
                                                step={0.05}
                                                className="gcm-cost-input gcm-pct-input"
                                                value={line.percent || ''}
                                                onChange={(e) => patchBondingLine(line.id, { percent: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)) })}
                                                placeholder="0"
                                            />
                                            <span className="gcm-pct-suffix">%</span>
                                        </div>
                                        <button
                                            type="button"
                                            className="gcm-bonding-remove"
                                            onClick={() => removeBondingLine(line.id)}
                                            aria-label="Remove bond"
                                            title="Remove bond"
                                        >
                                            <Trash2 size={13} />
                                        </button>
                                    </div>
                                ))}
                                <div className="gcm-bonding-actions">
                                    <button type="button" className="gcm-unlock-btn" onClick={addBondingLine}>
                                        <Plus size={12} /> Add bond type
                                    </button>
                                    {activeBondingLines(bonding).length === 0 && (
                                        <small>Enable at least one bond with a percent above zero for the line to appear.</small>
                                    )}
                                </div>
                                <div className="gcm-notes-wrap">
                                    <FileText size={12} />
                                    <input
                                        type="text"
                                        className="gcm-notes-input"
                                        value={bonding.clientDescription || ''}
                                        onChange={(e) => setBonding(prev => ({ ...prev, clientDescription: e.target.value }))}
                                        placeholder={`Client sub-copy (optional) — default: "Surety bond premium, ${formatBondingPercent(bondingPercent)} of the contract value"`}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer with totals */}
                <div className="gcm-footer">
                    <div className="gcm-totals">
                        {costs.projectOverheadPercentEnabled && projectOverheadPreview > 0 && (
                            <div className="gcm-total-row">
                                <span>
                                    Project overhead (
                                    {Math.max(0, Math.min(100, Number(costs.projectOverheadPercent) || 0))}
                                    %)
                                </span>
                                <strong className="has-value">{formatCurrency(projectOverheadPreview)}</strong>
                            </div>
                        )}
                        {estimatingTotal > 0 && (
                            <div className="gcm-total-row">
                                <span>Estimating / modeling</span>
                                <strong className="has-value">{formatCurrency(estimatingTotal)}</strong>
                            </div>
                        )}
                        <div className="gcm-total-row">
                            <span>Global dollar lines subtotal</span>
                            <strong className={runningTotal > 0 ? 'has-value' : ''}>{formatCurrency(runningTotal)}</strong>
                        </div>
                        {(runningTotal > 0 || projectOverheadPreview > 0 || estimatingTotal > 0) && (
                            <div className="gcm-total-row gcm-total-grand">
                                <span>Total (overhead % + estimating + dollar lines)</span>
                                <strong className={totalGlobalRawBeforeMargin > 0 ? 'has-value' : ''}>
                                    {formatCurrency(totalGlobalRawBeforeMargin)}
                                </strong>
                            </div>
                        )}
                        {costs.projectOverheadPercentEnabled && projectOverheadPreview > 0 && structureCount > 0 && (
                            <div className="gcm-total-row secondary">
                                <span>Per structure — project overhead ({structureCount})</span>
                                <span>{formatCurrency(Math.round(perStructureProjectOverhead))}</span>
                            </div>
                        )}
                        {runningTotal > 0 && structureCount > 0 && (
                            <div className="gcm-total-row secondary">
                                <span>Per structure — dollar lines ({structureCount})</span>
                                <span>{formatCurrency(Math.round(perStructureDollarLines))}</span>
                            </div>
                        )}
                        {bondingPercent > 0 && (
                            <div className="gcm-total-row secondary">
                                <span>Bonding ({formatBondingPercent(bondingPercent)}) — client line, not in the split</span>
                                <span>{clientSubtotal > 0 ? `≈ ${formatCurrency(bondingPreview)}` : '—'}</span>
                            </div>
                        )}
                    </div>
                    <div className="gcm-actions">
                        <button className="gcm-btn cancel" onClick={onClose}>Cancel</button>
                        <button className="gcm-btn save" onClick={handleSave}>
                            Save Global Costs
                        </button>
                    </div>
                </div>
            </div>

            {(quickLook || quickLookLoading) && (
                <div
                    className="gcm-quicklook-overlay"
                    onClick={(e) => {
                        e.stopPropagation();
                        setQuickLook(null);
                        setQuickLookLoading(false);
                    }}
                >
                    {quickLook ? (
                        <div className="gcm-quicklook" onClick={(e) => e.stopPropagation()}>
                            <header className="gcm-quicklook-head">
                                <span className="gcm-quicklook-name" title={quickLook.name}>
                                    {quickLook.name}
                                </span>
                                <span className="gcm-quicklook-hint">Space or Esc to close</span>
                                <a
                                    className="gcm-quicklook-open"
                                    href={quickLook.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    title="Open in a new tab"
                                >
                                    <ExternalLink size={14} />
                                </a>
                                <button
                                    type="button"
                                    className="gcm-quicklook-close"
                                    onClick={() => setQuickLook(null)}
                                    aria-label="Close preview"
                                >
                                    <X size={16} />
                                </button>
                            </header>
                            <iframe className="gcm-quicklook-frame" src={quickLook.url} title={quickLook.name} />
                            {markupRegions.length > 0 && (
                                <div className="gcm-quicklook-markup">
                                    <strong>Marked costs for this proposal</strong>
                                    <ul>
                                        {markupRegions.map((region, idx) => (
                                            <li key={`${region.pageNumber}-${idx}`}>
                                                Page {region.pageNumber}
                                                {region.label ? ` — ${region.label}` : ''}
                                                {region.amount != null ? ` (${formatCurrency(region.amount)})` : ''}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    ) : (
                        <Loader2 size={30} className="gcm-quicklook-spinner" />
                    )}
                </div>
            )}
        </div>
    );
};

export default GlobalCostsModal;
